/**
 * The Workspace's styled dropdown (same look as the Search account picker):
 * a trigger button and a floating menu with an optional filter box. The menu
 * is portalled to the page root (it carries the --ws-* colours) at fixed
 * coordinates so the scrolling settings panels it lives in never clip it. Plus the two pickers the settings panels share:
 * an Account picker and a multi-select for that Account's OnlyFans lists.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../../components/FeatherIcon';
import { LoadingBlock } from '../../components/Spinner';
import type { CrmAccount } from '../../crm/crmAccess';
import type { AccountColour } from '../colour';
import { Avatar } from './Avatar';
import { NO_AUTOFILL } from './SearchWidget';

interface DropdownProps {
  /** Trigger content (left of the chevron). */
  label: ReactNode;
  /** Shown above the items; filtering is the caller's (it gets the query). */
  filterPlaceholder?: string;
  children: (query: string, close: () => void) => ReactNode;
  disabled?: boolean;
  className?: string;
}

export function Dropdown({ label, filterPlaceholder, children, disabled, className = '' }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [at, setAt] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const close = () => setOpen(false);

  const place = () => {
    const r = trigger.current?.getBoundingClientRect();
    if (!r) return;
    const below = window.innerHeight - r.bottom - 12;
    const above = r.top - 12;
    const up = below < 220 && above > below;
    const maxHeight = Math.min(340, Math.max(160, up ? above : below));
    setAt({ top: up ? r.top - 4 - maxHeight : r.bottom + 4, left: r.left, width: Math.max(r.width, 240), maxHeight });
  };

  useLayoutEffect(() => {
    if (!open) return;
    setQuery('');
    place();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const outside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!menu.current?.contains(t) && !trigger.current?.contains(t)) setOpen(false);
    };
    // Follow the trigger while its panel scrolls; the window resizing closes it.
    const follow = (e: Event) => !menu.current?.contains(e.target as Node) && place();
    window.addEventListener('mousedown', outside);
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('mousedown', outside);
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={`ws-dd ${open ? 'is-open' : ''} ${className}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ws-dd-label">{label}</span>
        <FeatherIcon name="chevron-down" size={13} />
      </button>
      {open &&
        at &&
        createPortal(
          <div
            ref={menu}
            className="ws-menu ws-dd-menu"
            style={{ top: at.top, left: at.left, width: at.width, maxHeight: at.maxHeight }}
            // The settings panel underneath closes on outside presses; this menu is part of it.
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') return;
              // Close only this menu, not the panel it opened from.
              e.stopPropagation();
              setOpen(false);
              trigger.current?.focus();
            }}
          >
            {filterPlaceholder && <input {...NO_AUTOFILL} name="ws-dd-filter" autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={filterPlaceholder} />}
            <div className="ws-menu-scroll">{children(query.trim().toLowerCase(), close)}</div>
          </div>,
          trigger.current?.closest('.ws-page') ?? document.body,
        )}
    </>
  );
}

const nameOf = (a: CrmAccount | null | undefined) => a?.profile_name || a?.username || '';

/** Pick one Account; `badge` puts a count (e.g. attached lists) next to each. */
export function AccountDropdown({ accounts, value, onChange, colourOf, badge }: { accounts: CrmAccount[]; value: string; onChange: (id: string) => void; colourOf: (accountId: string) => AccountColour; badge?: (accountId: string) => number }) {
  const { t } = useTranslation('workspace');
  const cur = accounts.find((a) => a.account_id === value) ?? null;
  const n = cur && badge ? badge(cur.account_id) : 0;
  return (
    <Dropdown
      className="ws-dd--account"
      filterPlaceholder={accounts.length > 6 ? t('search.findAccount') : undefined}
      label={
        <>
          {cur && <Avatar src={cur.avatar_url} name={nameOf(cur)} size={18} ring={`0 0 0 1.5px ${colourOf(cur.account_id).accent}`} />}
          <span className="ws-dd-text">{cur ? nameOf(cur) : t('search.pickAccount')}</span>
          {n > 0 && <span className="ws-count">{n}</span>}
        </>
      }
    >
      {(q, close) => {
        const shown = accounts.filter((a) => !q || `${a.profile_name} ${a.username}`.toLowerCase().includes(q));
        if (!shown.length) return <span className="ws-menu-note">{t('dropdown.noMatch')}</span>;
        return shown.map((a) => {
          const count = badge ? badge(a.account_id) : 0;
          return (
            <button
              key={a.account_id}
              type="button"
              role="option"
              aria-selected={a.account_id === value}
              className={`ws-menu-item ${a.account_id === value ? 'is-on' : ''}`}
              onClick={() => {
                onChange(a.account_id);
                close();
              }}
            >
              <Avatar src={a.avatar_url} name={nameOf(a)} size={20} ring={`0 0 0 1.5px ${colourOf(a.account_id).accent}`} />
              <span className="ws-menu-label">{nameOf(a)}</span>
              {count > 0 ? <span className="ws-count">{count}</span> : <span className="ws-muted">@{a.username}</span>}
            </button>
          );
        });
      }}
    </Dropdown>
  );
}

export interface PickList {
  id: string;
  name: string;
  members?: number | null;
}

/** Multi-select of one Account's lists; the menu stays open while ticking. */
export function ListDropdown({ lists, isOn, onToggle, disabledTip }: { lists: PickList[] | null; isOn: (l: PickList) => boolean; onToggle: (l: PickList) => void; disabledTip?: (l: PickList) => string | null }) {
  const { t } = useTranslation('workspace');
  const picked = (lists ?? []).filter(isOn);
  const label =
    lists == null ? '…' : !lists.length ? t('activity.filters.noLists') : picked.length ? picked.map((l) => l.name).join(', ') : t('dropdown.chooseLists');
  return (
    <Dropdown
      className="ws-dd--lists"
      disabled={lists != null && !lists.length}
      filterPlaceholder={(lists?.length ?? 0) > 8 ? t('dropdown.findList') : undefined}
      label={
        <>
          <FeatherIcon name="list" size={13} />
          <span className="ws-dd-text">{label}</span>
          {picked.length > 0 && <span className="ws-count">{picked.length}</span>}
        </>
      }
    >
      {(q) => {
        if (lists == null) return <LoadingBlock pad="14px 0" size={18} label={t('activity.filters.loadingLists')} />;
        const shown = lists.filter((l) => !q || l.name.toLowerCase().includes(q));
        if (!shown.length) return <span className="ws-menu-note">{t('dropdown.noMatch')}</span>;
        return shown.map((l) => {
          const on = isOn(l);
          const tip = disabledTip?.(l) ?? null;
          const blocked = !!tip && !on;
          return (
            <button
              key={l.id}
              type="button"
              role="option"
              aria-selected={on}
              className={`ws-menu-item ${blocked ? 'is-disabled' : ''}`}
              title={tip ?? undefined}
              onClick={() => !blocked && onToggle(l)}
            >
              <span className="ws-check">{on && <FeatherIcon name="check" size={10} />}</span>
              <span className="ws-menu-label">{l.name}</span>
              {l.members != null && <span className="ws-chip-n">{l.members.toLocaleString('en-US')}</span>}
            </button>
          );
        });
      }}
    </Dropdown>
  );
}
